import { Point } from "./Point";
import { BezierSegmentControlPoint } from "./BezierSegmentControlPoint";
import type { IBezierCurveOptions } from "./IBezierCurveOptions";

export class BezierCurve
{
    
    svgPath: string;

    constructor( seriesData: number[], options: IBezierCurveOptions )
    {

        // If there is only one data point, duplicate it so we can draw a horizontal line
        if( seriesData.length == 1 )
        {
            seriesData.unshift( seriesData[ 0 ] - 0.001 ); // Need to alter the number slightly or the line wont render  
        }

        // Generate the SVG bezier path for the series data
        this.svgPath = this.generatePath( seriesData, options );

    }

    /**
     * Generate bezier curve for values
     * @param seriesData 
     * @returns 
     */
    generatePath( seriesData: number[], options: IBezierCurveOptions ): string
    {
        
        const points = this.getPoints( seriesData, options  );
        const controlPoints = this.getControlPoints( points );

        let point = points[ 0 ];
        let path = `M ${ options.padding } ${ point.Y } `;

        for( var i = 1; i < points.length; i++ )
        {
            point = points[ i ];
            const cp = controlPoints[ i - 1 ];
            if( i === 1 )
            {
                path += `C ${ cp.P1.X }, ${ cp.P1.Y }, ${ cp.P2.X }, ${ cp.P2.Y }, ${ point.X }, ${ point.Y } `;
                continue;
            }
            path += `S ${ cp.P2.X }, ${ cp.P2.Y }, ${ point.X }, ${ point.Y } `;
        }

        return path;
    }

    /**
     * Convert values to X,Y coordinates (top down for svg)
     * @param seriesData 
     * @returns 
     */
    getPoints( seriesData: number[], options: IBezierCurveOptions ): Point[]
    {
        const values = seriesData.slice( -options.maxPoints );
        const min = Math.min( ...values );
        const max = Math.max( ...values );

        const Xinterval = ( options.width - ( 2 * options.padding ) ) / ( values.length - 1 );
        const Yinterval = ( options.height - ( 2 * options.padding ) ) / ( max - min );

        let points: Point[] = [];

        for( let i = 0; i < values.length; i++ )
        {
            const x = ( i * Xinterval ) + options.padding;
            const y = ( ( max - values[ i ] ) * Yinterval ) + options.padding;

            points.push( new Point( x, y ) );
        }

        return points;
    }

    /**
     * Get bezier control points
     * https://github.com/Onaeem26/CubicBezierCurveSwift/blob/master/BezierCurve/BezierCurve/BezierConfiguration.swift
     * @param points 
     * @returns 
     */
    getControlPoints( points: Point[] ): BezierSegmentControlPoint[]
    {
        const segments = points.length - 1;

        if( segments === 0 )
        {
            return [];
        }

        if( segments === 1 )
        {
            return [ new BezierSegmentControlPoint( points[ 0 ], points[ 1 ] ) ];
        }

        let ad: number[] = [];
        let d: number[] = [];
        let bd: number[] = [];
        let rhsArray: Point[] = [];

        for( let i = 0; i < segments; i++ )
        {

            let rhsXValue = 0;
            let rhsYValue = 0;
            let p0 = points[ i ];
            let p3 = points[ i + 1 ];

            if( i === 0 )
            {
                bd.push( 0.0 );
                d.push( 2.0 );
                ad.push( 1.0 );

                rhsXValue = p0.X + ( 2 * p3.X );
                rhsYValue = p0.Y + ( 2 * p3.Y );

            }

            else if( i == segments - 1 )
            {
                bd.push( 2.0 );
                d.push( 7.0 );
                ad.push( 0.0 );

                rhsXValue = ( 8 * p0.X ) + p3.X;
                rhsYValue = ( 8 * p0.Y ) + p3.Y;
            }
                
            else
            {
                bd.push( 1.0 );
                d.push( 4.0 );
                ad.push( 1.0 );

                rhsXValue = ( 4 * p0.X ) + ( 2 * p3.X );
                rhsYValue = ( 4 * p0.Y ) + ( 2 * p3.Y );
            }

            rhsArray.push( new Point( rhsXValue, rhsYValue ) );

        }

        return this.thomasAlgorithm( bd, d, ad, rhsArray, segments, points );

    }

    /**
     * The Thomas Algorithm
     * @param bd 
     * @param d 
     * @param ad 
     * @param rhsArray 
     * @param segments 
     * @param points 
     * @returns 
     */
    thomasAlgorithm( bd: number[], d: number[], ad: number[], rhsArray: Point[], segments: number, points: Point[] ): BezierSegmentControlPoint[]
    {

        let controlPoints: BezierSegmentControlPoint[] = [];
        let solutionSet1: Point[] = new Array( segments );

        // First segment
        ad[ 0 ] = ad[ 0 ] / d[ 0 ];
        rhsArray[ 0 ].X = rhsArray[ 0 ].X / d[ 0 ];
        rhsArray[ 0 ].Y = rhsArray[ 0 ].Y / d[ 0 ];

        // Middle Elements
        if( segments > 2 )
        {
            for( let i = 1; i <= segments - 2; i++ )
            {
                let rhsValueX = rhsArray[ i ].X;
                let prevRhsValueX = rhsArray[ i - 1 ].X;

                let rhsValueY = rhsArray[ i ].Y;
                let prevRhsValueY = rhsArray[ i - 1 ].Y;

                ad[ i ] = ad[ i ] / ( d[ i ] - bd[ i ] * ad[ i - 1 ] );

                let exp1x = ( rhsValueX - ( bd[ i ] * prevRhsValueX ) );
                let exp1y = ( rhsValueY - ( bd[ i ] * prevRhsValueY ) );
                let exp2 = ( d[ i ] - bd[ i ] * ad[ i - 1 ] );

                rhsArray[ i ].X = exp1x / exp2;
                rhsArray[ i ].Y = exp1y / exp2;
            }
        }

        // Last Element
        let lastElementIndex = segments - 1;
        let exp1 = rhsArray[ lastElementIndex ].X - bd[ lastElementIndex ] * rhsArray[ lastElementIndex - 1 ].X;
        let exp1y = rhsArray[ lastElementIndex ].Y - bd[ lastElementIndex ] * rhsArray[ lastElementIndex - 1 ].Y;
        let exp2 = d[ lastElementIndex ] - bd[ lastElementIndex ] * ad[ lastElementIndex - 1 ];
        rhsArray[ lastElementIndex ].X = exp1 / exp2;
        rhsArray[ lastElementIndex ].Y = exp1y / exp2;

        solutionSet1[ lastElementIndex ] = rhsArray[ lastElementIndex ];

        for( let i = lastElementIndex - 1; i >= 0; i-- )
        {
            let controlPointX = rhsArray[ i ].X - ( ad[ i ] * solutionSet1[ i + 1 ]!.X );
            let controlPointY = rhsArray[ i ].Y - ( ad[ i ] * solutionSet1[ i + 1 ]!.Y );

            solutionSet1[ i ] = new Point( controlPointX, controlPointY );
        }

        let firstControlPoints = solutionSet1;
        let secondControlPoints: Point[] = [];

        for( let i = 0; i < segments; i++ )
        {
            if( i === ( segments - 1 ) )
            {
                let lastDataPoint = points[ i + 1 ];
                let controlPoint1 = firstControlPoints[ i ];

                let controlPoint2X = 0.5 * ( lastDataPoint.X + controlPoint1.X );
                let controlPoint2Y = 0.5 * ( lastDataPoint.Y + controlPoint1.Y );

                let controlPoint2 = new Point( controlPoint2X, controlPoint2Y );
                secondControlPoints.push( controlPoint2 );
            }
            else
            {
                let dataPoint = points[ i + 1 ];
                let controlPoint1 = firstControlPoints[ i + 1 ];

                let controlPoint2X = 2 * dataPoint.X - controlPoint1.X;
                let controlPoint2Y = 2 * dataPoint.Y - controlPoint1.Y;

                let controlPoint2 = new Point( controlPoint2X, controlPoint2Y );
                secondControlPoints.push( controlPoint2 );
            }
        }

        for( let i = 0; i <= segments; i++ )
        {
            let firstCP = firstControlPoints[ i ];
            let secondCP = secondControlPoints[ i ];

            let segmentControlPoint = new BezierSegmentControlPoint( firstCP, secondCP );
            controlPoints.push( segmentControlPoint );
        }

        return controlPoints;
    }

};